import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SeoMetadata from "@/components/common/SeoMetadata";
import { saveSeoMetadata } from "@/utils/seoUtils";
import { Link } from "react-router-dom";
import { House, ArrowRight, Calendar, Check, MapPin, DollarSign, CheckCircle, Building2, ShieldCheck, Home, ExternalLink, Gauge, Info } from "lucide-react";
import { LainaCta } from "@/components/common/LainaCta";
import { motion } from "framer-motion";
import ImageOverlay from "@/components/common/ImageOverlay";
import Image from "@/components/ui/image";
import { Button } from "@/components/ui/button";
import { AXO_REDIRECT_URL } from "@/services/api/constants";
import { preloadCriticalImages } from "@/utils/imageOptimizer";

// SEO Keywords for Cottage Loans
const seoKeywords = [
  "mökkilaina", "vapaa-ajan asunto laina", "kesämökki rahoitus", 
  "mökin osto laina", "vapaa-ajan asunto rahoitus", "edullinen mökkilaina",
  "mökin hankinta", "mökkilainan korko", "kesämökin ostaminen",
  "mökkilainan kilpailutus", "vapaa-ajan asunnon osto", "kesäasunto laina"
];

const MokkilainaPage = () => {
  const seoTitle = "Mökkilaina | Kilpailuta edullinen rahoitus vapaa-ajan asuntoon | Lainasampo";
  const seoDescription = "Vertaile ja kilpailuta mökkilaina Lainasampo.fi kautta. Löydä edullinen rahoitus kesämökin tai vapaa-ajan asunnon hankintaan ja toteuta unelmasi omasta lomapaikasta.";
  const pageUrl = "https://lainasampo.fi/lainat/mokkilaina";
  const heroImage = "/lovable-uploads/994c2c96-3fe4-4773-bbe9-6221d7741b32.png";
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  useEffect(() => {
    // Esilataa hero-kuva ennen muita elementtejä
    preloadCriticalImages([heroImage], true);
    
    saveSeoMetadata("/lainat/mokkilaina", {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SeoMetadata
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={pageUrl}
      />
      
      <section className="relative w-full min-h-[440px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Kuva latautuu ensimmäisenä priority=true -parametrin ansiosta */}
        <Image
          src={heroImage}
          alt="Mökki järven rannalla"
          className="absolute inset-0 w-full h-full object-cover object-center"
          priority={true}
          style={{ zIndex: 1 }}
        />
        {/* Enhanced overlay with better contrast for text visibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-black/40 pointer-events-none" style={{zIndex: 2}}></div>
        <div className="relative z-30 w-full px-4 md:px-12 max-w-5xl mx-auto flex flex-col items-center justify-center py-16 md:py-28 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white drop-shadow-lg mb-4">
            Mökkilaina&nbsp;
            <span className="text-gray-200 font-light">— helppo rahoitus vapaa-ajan asunnolle</span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg md:text-2xl text-white mb-8 drop-shadow-sm bg-black/30 py-2 px-3 rounded-lg">
            Vertaile mökkilainat yhdestä paikasta. Kilpailuta tarjoukset ja löydä kustannustehokkain ratkaisu loma-asunnon rahoitukseen ilman sitoumuksia.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-4 justify-center">
            <LainaCta 
              text="Hae mökkilainaa"
              variant="primary"
              size="lg"
              className="w-full sm:w-auto"
            />
            <a
              href="/artikkelit/mokin-ylpitovinkit"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center bg-white/90 hover:bg-white text-blue-700 font-medium px-4 py-2 rounded-lg hover:underline gap-2"
            >
              <Info className="h-5 w-5" />
              Lue vinkit mökin ylläpitoon
              <ExternalLink className="h-4 w-4" />
            </a>
            <a
              href="/lainat/asuntolaina"
              className="flex items-center bg-white/90 hover:bg-white text-[#694F36] font-medium px-4 py-2 rounded-lg hover:underline gap-2"
            >
              Katso myös asuntolaina
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          
          {/* Improved grid layout for feature boxes with better visibility on all screen sizes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm md:text-base font-medium mt-8">
            <div className="bg-white/95 border border-blue-100 px-3 py-2 md:px-4 md:py-3 rounded-xl text-blue-800 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span>Nopea päätös</span>
            </div>
            <div className="bg-white/95 border border-blue-100 px-3 py-2 md:px-4 md:py-3 rounded-xl text-blue-800 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-green-600" />
              <span>Joustava laina-aika</span>
            </div>
            <div className="bg-white/95 border border-blue-100 px-4 py-3 rounded-xl text-blue-800 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <span className="text-xs md:text-sm font-semibold whitespace-nowrap">Lainasumma 5 000–70 000 €</span>
            </div>
            <div className="bg-white/95 border border-blue-100 px-3 py-2 md:px-4 md:py-3 rounded-xl text-blue-800 flex items-center gap-2">
              <Gauge className="h-5 w-5 text-green-600" />
              <span>Kilpailukykyinen korko</span>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="mb-8">
              <div className="flex flex-col md:flex-row gap-6 items-center bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100 shadow-sm">
                <div className="md:w-1/4 flex justify-center">
                  <Image
                    src="/lovable-uploads/0b1d2b0b-29b3-4c60-a2c5-1b91ff908fac.png"
                    alt="Etua.fi logo"
                    width={200}
                    height={100}
                    className="mx-auto"
                    objectFit="contain"
                  />
                </div>
                <div className="md:w-3/4">
                  <h2 className="text-xl font-bold text-blue-800 mb-2">Kilpailuta isompi mökkilainasi Etua.fi:n kautta</h2>
                  <p className="text-gray-700 mb-4">
                    Jos haet yli 70 000€ mökkilainaa, voit kilpailuttaa sen maksutta Etua.fi:n kautta. Kilpailutuksella voit säästää jopa tuhansia euroja. Etua.fi on toiminut vuodesta 2008 ja on Suomen pisimpään toiminut lainavertailupalvelu.
                  </p>
                  <div className="flex flex-wrap gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Ilmainen palvelu</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Useita pankkeja</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Finanssivalvonnan hyväksymä</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild className="bg-blue-600 hover:bg-blue-700">
                      <a 
                        href="https://go.adt253.net/t/t?a=1749114620&as=1854031059&t=2&tk=1" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Kilpailuta yli 70 000€ laina
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="border-blue-500 text-blue-600">
                      <Link to="/lainat/remonttilaina">
                        Tarvitsetko remonttilainaa?
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-100 shadow-sm mb-8">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="md:w-1/4 flex justify-center">
                  <Image
                    src="/lovable-uploads/01da9111-1348-4bf2-bea4-df914538f8d3.png"
                    alt="Bluestep Bank logo"
                    width={200}
                    height={100}
                    className="mx-auto"
                    objectFit="contain"
                  />
                </div>
                <div className="md:w-3/4">
                  <h2 className="text-xl font-bold text-blue-800 mb-2">Bluestep Bank - Mökkilaina myös haastaviin tilanteisiin</h2>
                  <p className="text-gray-700 mb-4">
                    Eikö perinteinen pankki myöntänyt sinulle mökkilainaa? Bluestep Bank käsittelee jokaisen hakemuksen yksilöllisesti ja voi tarjota rahoitusta, vaikka luottotiedoissasi olisi merkintöjä tai tulosi olisivat epäsäännölliset.
                  </p>
                  <div className="flex flex-wrap gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Joustava lainapäätös</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Yksilöllinen käsittely</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Jopa 85% vakuusarvo</span>
                    </div>
                  </div>
                  <Button asChild className="bg-blue-600 hover:bg-blue-700">
                    <a 
                      href="https://go.adt256.com/t/t?a=1540469708&as=1854031059&t=2&tk=1" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Tutustu Bluestep Bankin mökkilainaan
                    </a>
                  </Button>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Mökkilainan edut
                </span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    icon: <Calendar className="h-6 w-6 text-blue-600" />,
                    title: "Nopea käsittely",
                    description: "Saat lainapäätöksen nopeasti ja pääset toteuttamaan unelmaasi"
                  },
                  {
                    icon: <DollarSign className="h-6 w-6 text-blue-600" />,
                    title: "Kilpailukykyinen korko",
                    description: "Vertaile tarjouksia ja löydä edullisin rahoitusvaihtoehto"
                  },
                  {
                    icon: <House className="h-6 w-6 text-blue-600" />,
                    title: "Joustava vakuus",
                    description: "Mökki voi toimia lainan vakuutena, mikä mahdollistaa edullisemman koron"
                  },
                  {
                    icon: <CheckCircle className="h-6 w-6 text-blue-600" />,
                    title: "Selkeät ehdot",
                    description: "Läpinäkyvät lainaehdot ja ei piilokustannuksia"
                  }
                ].map((benefit, index) => (
                  <Card key={index} className="border-blue-100">
                    <CardContent className="p-4 flex gap-4">
                      <div className="mt-1">{benefit.icon}</div>
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-1">{benefit.title}</h3>
                        <p className="text-gray-600 text-sm">{benefit.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Näin haet mökkilainaa
                </span>
              </h2>
              
              <div className="space-y-4">
                {[
                  {
                    step: 1,
                    title: "Täytä hakemus verkossa",
                    description: "Täytä mökkilainahakemus Lainasampo.fi:n kautta. Hakemus vie vain muutaman minuutin."
                  },
                  {
                    step: 2,
                    title: "Vertaile tarjouksia",
                    description: "Saat useita tarjouksia eri pankeilta ja voit vertailla korkoja, laina-aikoja sekä muita ehtoja."
                  },
                  {
                    step: 3,
                    title: "Valitse sopivin tarjous",
                    description: "Valitse tarjouksista itsellesi sopivin. Voit kysyä neuvoa myös asiakaspalvelustamme."
                  },
                  {
                    step: 4,
                    title: "Nauti omasta mökistäsi",
                    description: "Saatuasi rahoituksen voit hankkia unelmiesi vapaa-ajan asunnon ja nauttia siitä vuosien ajan."
                  }
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    className="flex items-start p-5 bg-white rounded-lg shadow-sm border border-gray-100"
                  >
                    <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 shrink-0">{step.step}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-blue-700 mb-1">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-8 text-center">
                <LainaCta 
                  text="Aloita mökkilainan hakeminen" 
                  to={AXO_REDIRECT_URL}
                  variant="primary" 
                  size="lg" 
                  className="px-8"
                />
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Suosituimmat mökkialueet Suomessa
                </span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    image: "/lovable-uploads/2df5092a-5f80-46c2-9d8c-5ca53ff489b4.png",
                    location: "Etelä-Savo",
                    title: "Saimaan alue",
                    description: "Saimaan alue tarjoaa kauniit järvimaisemat ja monipuoliset aktiviteettimahdollisuudet.",
                    tags: ["Hyvät kalastusmahdollisuudet", "Veneilymahdollisuudet"]
                  },
                  {
                    image: "/lovable-uploads/7ead0c3b-d7fe-4fdb-8426-6d0ffb3939a0.png",
                    location: "Lappi",
                    title: "Lapin tunturialueet",
                    description: "Lapissa voit nauttia talviurheilusta ja kesällä yöttömästä yöstä luonnon keskellä.",
                    tags: ["Hiihtomahdollisuudet", "Revontulet"]
                  },
                  {
                    image: "/lovable-uploads/0d242a43-b6db-416f-94c6-f33fced00b5c.png",
                    location: "Lounais-Suomi",
                    title: "Saaristomeri",
                    description: "Turun saaristo ja Ahvenanmaa tarjoavat ainutlaatuisen ympäristön saaristomökkeilylle.",
                    tags: ["Veneilymahdollisuudet", "Merellinen ilmasto"]
                  }
                ].map((location, index) => (
                  <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="relative h-48">
                      <ImageOverlay 
                        imageUrl={location.image} 
                        alt={location.title} 
                        className="h-full w-full"
                        onImageChange={(newUrl) => console.log(newUrl)}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                      <div className="absolute bottom-3 left-3 text-white">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1" />
                          <span className="text-sm">{location.location}</span>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="text-lg font-semibold text-blue-700 mb-2">{location.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{location.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {location.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">{tag}</span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Yleisimmät kysymykset mökkilainasta
                </span>
              </h2>
              
              <div className="space-y-4">
                {[
                  {
                    question: "Kuinka paljon voin saada mökkilainaa?",
                    answer: "Mökkilainan määrä riippuu tulotasostasi, luottotiedoistasi sekä hankittavan mökin arvosta. Tyypillisesti mökkilainat ovat 10 000€ - 300 000€ välillä, ja pankki voi rahoittaa yleensä 60-75% mökin hankintahinnasta."
                  },
                  {
                    question: "Mitä dokumentteja tarvitsen mökkilainan hakemiseen?",
                    answer: "Tarvitset yleensä tiedot tuloistasi (palkkakuitit tai verotiedot), tiedot muista lainoistasi, sekä tiedot hankittavasta kiinteistöstä. Jos kyseessä on jo olemassa oleva mökki, tarvitaan myös kiinteistön arviointi."
                  },
                  {
                    question: "Millainen korko mökkilainassa on?",
                    answer: "Mökkilainan korko on tyypillisesti hieman korkeampi kuin asuntolainan korko, mutta matalampi kuin kulutusluoton korko. Korko voi olla kiinteä tai sidottu viitekorkoon (kuten euribor). Kilpailuttamalla lainan voit saada edullisemman koron."
                  },
                  {
                    question: "Voinko saada mökkilainaa, jos minulla on jo asuntolainaa?",
                    answer: "Kyllä, voit hakea mökkilainaa, vaikka sinulla olisi jo asuntolainaa. Pankki arvioi kokonaistilanteesi, mukaan lukien kaikki olemassa olevat lainat ja tulotasosi, varmistaakseen että pystyt hoitamaan lainanlyhennykset."
                  }
                ].map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    className="bg-white rounded-lg shadow-sm p-5"
                  >
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-6 text-center">
                <Link to="/ukk" className="text-blue-600 font-medium hover:underline flex items-center justify-center">
                  Katso kaikki usein kysytyt kysymykset
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <Card className="border-blue-100">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-lg font-bold text-blue-800">Mökkilainan tiedot</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lainasumma</span>
                    <span className="font-medium">5 000€ - 70 000€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Laina-aika</span>
                    <span className="font-medium">1 - 15 vuotta</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Korko alkaen</span>
                    <span className="font-medium">4.9%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kuukausimaksu</span>
                    <span className="font-medium">5€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Toimitusmaksu</span>
                    <span className="font-medium">0€ - 150€</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-blue-100 bg-gradient-to-br from-blue-50 to-purple-50">
              <CardHeader>
                <CardTitle className="text-blue-800">Mitä mökkilainalla voi rahoittaa?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Home className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-800">Kesämökki</h3>
                    <p className="text-sm text-gray-600">Perinteinen suomalainen kesämökki järven rannalla</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-800">Vapaa-ajan asunto</h3>
                    <p className="text-sm text-gray-600">Ympärivuotiseen käyttöön soveltuva mökki</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Home className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-800">Mökin remontointi</h3>
                    <p className="text-sm text-gray-600">Olemassa olevan mökin kunnostus</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Home className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-800">Tontti</h3>
                    <p className="text-sm text-gray-600">Mökkitontin hankinta rakentamista varten</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-blue-100">
              <CardHeader>
                <CardTitle className="text-blue-800">Mökkilainan hakeminen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Mökkilainan hakeminen on helppoa ja nopeaa. Täytä hakemus verkossa ja saat lainatarjoukset nopeasti.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm">Ilmainen hakemus</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm">Ei sitoutumispakkoa</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm">Nopea käsittely</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm">Useita tarjouksia</span>
                  </div>
                </div>
                <div className="pt-2">
                  <LainaCta 
                    text="Hae mökkilainaa" 
                    variant="primary" 
                    size="md" 
                    className="w-full" 
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="py-10 md:py-16">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-8 md:p-10 text-white text-center max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Toteuta unelmasi omasta mökistä</h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Kilpailuta mökkilainatarjoukset Lainasampo.fi:n kautta ja löydä edullisin rahoitus vapaa-ajan asunnollesi. Täytä yksi hakemus ja saat useita tarjouksia!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LainaCta 
                text="Kilpailuta mökkilaina nyt" 
                to={AXO_REDIRECT_URL}
                variant="primary" 
                size="lg" 
                className="bg-white text-blue-600" 
              />
              <Link to="/lainat/remonttilaina" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-3 rounded-lg inline-flex items-center justify-center">
                Katso myös remonttilaina
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MokkilainaPage;
